import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import PageLayout from '../Shared/PageLayout';
import { useNavigate } from 'react-router-dom';
import Icon from '../Shared/Icons';
import AnnouncementsWidget from '../Shared/AnnouncementsWidget';
import { PROGRAMS } from '../../config/programs';
import { fetchMembers, fetchReports } from '../../utils/programData';
import { isoWeekKey } from '../../utils/weekUtils';

// Safely fetch a collection without letting one failure block the rest of
// the dashboard — this was the bug: sequential awaits + a single try/catch
// meant one blocked/missing collection zeroed out EVERYTHING, including
// students & parents counts that were actually working fine.
async function safeDocs(collectionName) {
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error(`Dashboard: could not read "${collectionName}"`, err);
    return [];
  }
}

// Staff members live inside "users" (filtered by role), not a separate
// "staff" collection — this was silently returning 0 before.
async function safeStaffDocs() {
  try {
    const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'staff')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Dashboard: could not read staff users', err);
    return [];
  }
}

export default function Dashboard() {
  const [stats, setStats] = useState({
    students: 0, parents: 0, staff: 0, sessions: 0, reports: 0,
    chessStudents: 0, chessGamesFinished: 0, chessGamesLive: 0, meetings: 0,
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [recentParents, setRecentParents] = useState([]);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [activityTab, setActivityTab] = useState('students');
  const [programRows, setProgramRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setLoading(true);

    const [
      students, parents, staff, sessions, reports,
      chessStudents, chessGames, meetings,
    ] = await Promise.all([
      safeDocs('students'),
      safeDocs('parents'),
      safeStaffDocs(),
      safeDocs('sessions'),
      safeDocs('reports'),
      safeDocs('chessStudents'),
      safeDocs('chessGames'),
      safeDocs('meetings'),
    ]);

    setStats({
      students: students.length,
      parents: parents.length,
      staff: staff.length,
      sessions: sessions.length,
      reports: reports.length,
      chessStudents: chessStudents.length,
      chessGamesFinished: chessGames.filter(g => g.status === 'finished').length,
      chessGamesLive: chessGames.filter(g => g.status === 'active').length,
      meetings: meetings.length,
    });

    // Sort client-side (robust even when some docs are missing createdAt)
    const byCreatedDesc = (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    setRecentStudents([...students].sort(byCreatedDesc).slice(0, 6));
    setRecentParents([...parents].sort(byCreatedDesc).slice(0, 6));
    setRecentMeetings([...meetings].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 6));

    loadPrograms();
    setLoading(false);
  }

  // ── Muhtasari wa kila programu (rejista mpya) ──────────────────────
  async function loadPrograms() {
    const wk = isoWeekKey();
    const rows = [];
    for (const p of PROGRAMS) {
      try {
        const [members, week] = await Promise.all([
          fetchMembers(p.id),
          fetchReports({ programId: p.id, kind: 'individual', weekKey: wk }),
        ]);
        rows.push({
          program: p,
          members: members.length,
          done: week.length,
          pending: Math.max(0, members.length - week.length),
        });
      } catch (err) {
        console.error('Dashboard: program summary failed', p.id, err);
        rows.push({ program: p, members: 0, done: 0, pending: 0 });
      }
    }
    setProgramRows(rows);
  }

  const foundationCards = [
    { emoji: '', value: stats.students, label: 'Students Enrolled', color: '#14452F', onClick: () => navigate('/admin/students') },
    { emoji: '', value: stats.parents, label: 'Families Supported', color: '#E65100', onClick: () => navigate('/admin/parents') },
    { emoji: '', value: stats.staff, label: 'Active Staff', color: '#00695C', onClick: () => navigate('/admin/staff') },
    { emoji: '', value: stats.sessions, label: 'Sessions Held', color: '#4527A0', onClick: () => navigate('/admin/sessions') },
  ];

  const programCards = [
    { emoji: '', value: stats.reports, label: 'Progress Reports', color: '#1565C0', onClick: () => navigate('/admin/reports') },
    { emoji: '', value: stats.meetings, label: 'Vikao Recorded', color: '#0277BD', onClick: () => navigate('/admin/meetings') },
    { emoji: '', value: stats.chessStudents, label: 'Chess Students', color: '#F57C00', onClick: () => navigate('/admin/chess-students') },
    { emoji: '🔴', value: stats.chessGamesLive, label: 'Live Chess Games', color: '#C62828', onClick: () => navigate('/chess') },
  ];

  const activityData = {
    students: recentStudents,
    parents: recentParents,
    meetings: recentMeetings,
  };

  return (
    <PageLayout>
      <div>

        {/* HEADER */}
        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Dashboard</div>
            <div style={styles.pageSub}>
              {new Date().toDateString()} · Welcome back, Admin
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate('/programs')}
          >
            Fungua Programu
          </button>
        </div>

        <AnnouncementsWidget limitTo={4} />

        {/* PROGRAMU ZETU */}
        <div style={styles.sectionLabel}>Programu Zetu</div>
        <div style={{ marginBottom: '26px' }}>
          {programRows.map(row => (
            <div
              key={row.program.id}
              onClick={() => navigate(`/programs/${row.program.id}`)}
              style={{
                background: '#fff', border: '1px solid #E8E4DA', borderRadius: '14px',
                padding: '15px 18px', marginBottom: '10px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '12px',
                borderLeft: `4px solid ${row.program.color}`,
              }}
            >
              <div style={{
                width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
                background: `${row.program.color}15`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={row.program.icon} size={17} color={row.program.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}>
                  {row.program.name}
                </div>
                <div style={{ fontSize: '11.5px', color: '#6B7280', marginTop: '3px' }}>
                  Wanachama {row.members} · ripoti {row.done} wiki hii
                </div>
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '99px',
                color: row.pending ? '#D97706' : '#059669',
                background: row.pending ? '#FFFBEB' : '#ECFDF5', whiteSpace: 'nowrap',
              }}>
                {row.pending ? `${row.pending} bado` : 'Kamili'}
              </span>
            </div>
          ))}
        </div>

        {/* FOUNDATION IMPACT */}
        <div style={styles.sectionLabel}>Foundation Impact</div>
        <div style={styles.statsGrid}>
          {foundationCards.map((s, i) => (
            <div key={i} style={styles.statCard} onClick={s.onClick}>
              <div style={{ ...styles.statBar, background: `linear-gradient(90deg, ${s.color}, ${s.color}99)` }} />
              <div style={styles.statEmoji}>{s.emoji}</div>
              <div style={styles.statVal}>{loading ? '—' : s.value}</div>
              <div style={styles.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* PROGRAMS */}
        <div style={styles.sectionLabel}>Programs</div>
        <div style={styles.statsGrid}>
          {programCards.map((s, i) => (
            <div key={i} style={styles.statCard} onClick={s.onClick}>
              <div style={{ ...styles.statBar, background: `linear-gradient(90deg, ${s.color}, ${s.color}99)` }} />
              <div style={styles.statEmoji}>{s.emoji}</div>
              <div style={styles.statVal}>{loading ? '—' : s.value}</div>
              <div style={styles.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* RECENT ACTIVITY — grouped behind tabs so the page doesn't feel cluttered */}
        <div style={styles.panel}>
          <div style={styles.panelTitle}>
            <span>Recent Activity</span>
          </div>
          <div style={styles.activityTabs}>
            {[
              { id: 'students', label: `Students (${recentStudents.length})` },
              { id: 'parents', label: `Parents (${recentParents.length})` },
              { id: 'meetings', label: `Vikao (${recentMeetings.length})` },
            ].map(t => (
              <button
                key={t.id}
                style={{ ...styles.activityTab, ...(activityTab === t.id ? styles.activityTabActive : {}) }}
                onClick={() => setActivityTab(t.id)}
              >
                {t.label}
              </button>
            ))}
            <span
              style={styles.panelLink}
              onClick={() => navigate(
                activityTab === 'students' ? '/admin/students' :
                activityTab === 'parents' ? '/admin/parents' : '/admin/meetings'
              )}
            >
              View all →
            </span>
          </div>

          {activityData[activityTab].length === 0 ? (
            <div style={styles.empty}>Nothing here yet.</div>
          ) : activityTab === 'meetings' ? (
            recentMeetings.map(m => (
              <div key={m.id} style={styles.listItem}>
                <div style={{ ...styles.avatar, background: 'linear-gradient(135deg, #0277BD, #4FC3F7)' }}></div>
                <div style={styles.liInfo}>
                  <div style={styles.liName}>{m.title}</div>
                  <div style={styles.liMeta}>{m.date} · {m.meetingType}</div>
                </div>
              </div>
            ))
          ) : (
            activityData[activityTab].map((p) => (
              <div key={p.id} style={styles.listItem}>
                <div style={styles.avatar}>
                  {p.photoURL ? (
                    <img src={p.photoURL} alt={p.firstName} style={styles.avatarImg} />
                  ) : (
                    <span>{p.firstName?.[0]}{p.lastName?.[0]}</span>
                  )}
                </div>
                <div style={styles.liInfo}>
                  <div style={styles.liName}>{p.firstName} {p.lastName}</div>
                  <div style={styles.liMeta}>
                    {activityTab === 'students'
                      ? `${p.studentId || ''} · Class ${p.class} · ${p.school}`
                      : `${p.phone || ''} · ${p.region || ''}`}
                  </div>
                </div>
                <span style={styles.badgeGreen}>{p.status || 'Active'}</span>
              </div>
            ))
          )}
        </div>

      </div>
    </PageLayout>
  );
}

const styles = {
  pageHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '20px', flexWrap: 'wrap', gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  btnPrimary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)', color: '#fff',
    border: 'none', padding: '10px 20px', borderRadius: '10px',
    fontFamily: 'inherit', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', boxShadow: '0 4px 14px rgba(245,124,0,0.25)',
  },
  sectionLabel: {
    fontSize: '11.5px', fontWeight: '700', letterSpacing: '0.5px',
    textTransform: 'uppercase', color: '#6B7280', margin: '18px 0 10px',
  },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '14px', marginBottom: '8px',
  },
  statCard: {
    background: '#fff', borderRadius: '16px', padding: '18px 16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)',
    position: 'relative', overflow: 'hidden', cursor: 'pointer',
  },
  statBar: { height: '3px', borderRadius: '3px 3px 0 0', position: 'absolute', top: 0, left: 0, right: 0 },
  statEmoji: { fontSize: '22px', marginBottom: '8px' },
  statVal: {
    fontFamily: "'Fraunces', Georgia, serif", fontSize: '27px', lineHeight: 1, marginBottom: '3px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  statLbl: { fontSize: '11.5px', color: '#6B7280' },
  panel: {
    background: '#fff', borderRadius: '16px', padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)',
    marginTop: '20px', marginBottom: '20px',
  },
  panelTitle: {
    fontSize: '13.5px', fontWeight: '600', marginBottom: '14px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  activityTabs: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px',
  },
  activityTab: {
    padding: '7px 14px', borderRadius: '20px', border: '1.5px solid #E5E7EB',
    background: 'transparent', color: '#6B7280', fontSize: '12px',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  activityTabActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent', color: '#fff',
  },
  panelLink: { fontSize: '12px', color: '#F57C00', cursor: 'pointer', fontWeight: '500', marginLeft: 'auto' },
  empty: { textAlign: 'center', color: '#6B7280', fontSize: '13px', padding: '20px 0' },
  listItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 0', borderBottom: '1px solid #F3F4F6',
  },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '600', color: '#fff', flexShrink: 0, overflow: 'hidden',
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  liInfo: { flex: 1 },
  liName: { fontSize: '13.5px', fontWeight: '500' },
  liMeta: { fontSize: '11.5px', color: '#6B7280', marginTop: '1px' },
  badgeGreen: {
    fontSize: '10.5px', padding: '3px 9px', borderRadius: '20px',
    fontWeight: '500', background: '#EAF3EC', color: '#14452F',
  },
};
